import React, { createContext, useMemo, useState } from 'react';
import { SidebarItemPair } from 'app/shared/layout/sidebar/configuration/sidebar-configuration';
import MainSidebarWrapper from 'app/shared/layout/main-sidebar/main-sidebar-wrapper';
import { useLocation } from 'react-router-dom';
import { useAppSelector } from 'app/config/store';
import { MANDATES } from 'app/config/constants';

export const MainSidebarContext = createContext({ sidebarItem: null, setSidebarItem: null });

const MainSidebarContextWrapper = ({
  children,
  isAuthenticated,
  hasAnonymousRole,
  showOnlyOnRoute = undefined,
  shouldShowCreateCaseTab = true,
}) => {
  const location = useLocation();

  const szSubjectOfRepresentationSelected = useAppSelector(state => state.authentication.account.szSubjectOfRepresentationSelected);
  const mandate = useAppSelector<string[] | undefined>(state => state.authentication.account.mandate);
  // Kad korisnik radi u ime drugog subjekta, akcije Predaja zahtjeva i Predaj pismeno (kao i otvaranje detalja) ovise o eOvlaštenjima.
  const hasSubmissionMandate = szSubjectOfRepresentationSelected !== true || (mandate ?? []).includes(MANDATES.SUBMISSION);

  const checkRoute = () => {
    const sidebarPath = location.pathname.substring(location.pathname.lastIndexOf('/') + 1);
    return sidebarPath === showOnlyOnRoute;
  };

  const tabs = useMemo(
    () => [
      {
        sidebarItemPath: '/',
        sidebarItemValue: 'Početna stranica',
        collapsed: false,
        readonly: false,
        visible: true,
        children: [],
        canViewDocument: false,
        canView: false,
        canEdit: false,
        canCopy: false,
        canSign: false,
        canDelete: false,
        canAddAttachment: false,
        canViewAttachments: false,
        canViewSignatures: false,
      },
      {
        sidebarItemPath: '/case-submission',
        sidebarItemValue: 'Predaja zahtjeva',
        collapsed: false,
        readonly: false,
        visible: isAuthenticated && shouldShowCreateCaseTab && hasSubmissionMandate,
        children: [],
        canViewDocument: false,
        canView: false,
        canEdit: false,
        canCopy: false,
        canSign: false,
        canDelete: false,
        canAddAttachment: false,
        canViewAttachments: false,
        canViewSignatures: false,
      },
      {
        sidebarItemPath: '/cases',
        sidebarItemValue: 'Predmeti',
        collapsed: false,
        readonly: true,
        visible: isAuthenticated && !hasAnonymousRole && shouldShowCreateCaseTab,
        canViewDocument: false,
        canView: false,
        canEdit: false,
        canCopy: false,
        canSign: false,
        canDelete: false,
        canAddAttachment: false,
        canViewAttachments: false,
        canViewSignatures: false,
        children: [
          {
            sidebarItemPath: '/my-cases',
            sidebarItemValue: 'Moji predmeti',
            collapsed: false,
            readonly: false,
            visible: isAuthenticated && !hasAnonymousRole && shouldShowCreateCaseTab,
            children: [],
            canViewDocument: false,
            canView: false,
            canEdit: false,
            canCopy: false,
            canSign: false,
            canDelete: false,
            canAddAttachment: false,
            canViewAttachments: false,
            canViewSignatures: false,
          },
          {
            sidebarItemPath: '/participat-cases',
            sidebarItemValue: 'Predmeti u kojima sudjelujem',
            collapsed: false,
            readonly: false,
            visible: isAuthenticated && !hasAnonymousRole && shouldShowCreateCaseTab,
            children: [],
            canViewDocument: false,
            canView: false,
            canEdit: false,
            canCopy: false,
            canSign: false,
            canDelete: false,
            canAddAttachment: false,
            canViewAttachments: false,
            canViewSignatures: false,
          },
        ],
      },
      {
        sidebarItemPath: '/case-file-inspection',
        sidebarItemValue: 'Uvid u spis predmeta',
        collapsed: false,
        readonly: false,
        visible: true,
        children: [],
        canViewDocument: false,
        canView: false,
        canEdit: false,
        canCopy: false,
        canSign: false,
        canDelete: false,
        canAddAttachment: false,
        canViewAttachments: false,
        canViewSignatures: false,
      },
      {
        sidebarItemPath: '/notice-board',
        sidebarItemValue: 'Oglasna ploča',
        collapsed: false,
        readonly: false,
        visible: true,
        children: [],
        canViewDocument: false,
        canView: false,
        canEdit: false,
        canCopy: false,
        canSign: false,
        canDelete: false,
        canAddAttachment: false,
        canViewAttachments: false,
        canViewSignatures: false,
      },
      {
        sidebarItemPath: 'https://katalog.mgipu.hr/search',
        sidebarItemValue: 'Prostorni planovi',
        collapsed: false,
        readonly: false,
        visible: true,
        children: [],
        newTab: true,
        canViewDocument: false,
        canView: false,
        canEdit: false,
        canCopy: false,
        canSign: false,
        canDelete: false,
        canAddAttachment: false,
        canViewAttachments: false,
        canViewSignatures: false,
      },
      {
        sidebarItemPath: '/current-regulations',
        sidebarItemValue: 'Važeći propisi',
        collapsed: false,
        readonly: false,
        visible: true,
        children: [],
        canViewDocument: false,
        canView: false,
        canEdit: false,
        canCopy: false,
        canSign: false,
        canDelete: false,
        canAddAttachment: false,
        canViewAttachments: false,
        canViewSignatures: false,
      },
      {
        sidebarItemPath: '/administrative-unit-overview',
        sidebarItemValue: 'Nadležni uredi',
        collapsed: false,
        readonly: false,
        visible: true,
        children: [],
        canViewDocument: false,
        canView: false,
        canEdit: false,
        canCopy: false,
        canSign: false,
        canDelete: false,
        canAddAttachment: false,
        canViewAttachments: false,
        canViewSignatures: false,
      },
      {
        sidebarItemPath: '/public-authority-overview',
        sidebarItemValue: 'Javnopravna tijela',
        collapsed: false,
        readonly: false,
        visible: true,
        children: [],
        canViewDocument: false,
        canView: false,
        canEdit: false,
        canCopy: false,
        canSign: false,
        canDelete: false,
        canAddAttachment: false,
        canViewAttachments: false,
        canViewSignatures: false,
      },
      {
        sidebarItemPath: '/chamber-member-overview',
        sidebarItemValue: 'Projektanti',
        collapsed: false,
        readonly: false,
        visible: true,
        children: [],
        canViewDocument: false,
        canView: false,
        canEdit: false,
        canCopy: false,
        canSign: false,
        canDelete: false,
        canAddAttachment: false,
        canViewAttachments: false,
        canViewSignatures: false,
      },
      {
        sidebarItemPath: '/notifications',
        sidebarItemValue: 'Obavijesti',
        collapsed: false,
        readonly: false,
        visible: true,
        children: [],
        canViewDocument: false,
        canView: false,
        canEdit: false,
        canCopy: false,
        canSign: false,
        canDelete: false,
        canAddAttachment: false,
        canViewAttachments: false,
        canViewSignatures: false,
      },
    ],
    [isAuthenticated, hasAnonymousRole, shouldShowCreateCaseTab, hasSubmissionMandate],
  );

  const [sidebarItem, setSidebarItem] = useState<SidebarItemPair>(null);
  const filteredTabs = tabs.filter(tab => tab.visible);

  return (
    <MainSidebarContext.Provider value={{ sidebarItem, setSidebarItem }}>
      {showOnlyOnRoute === undefined || checkRoute() ? (
        <MainSidebarWrapper navigationItems={filteredTabs}>{children}</MainSidebarWrapper>
      ) : (
        children
      )}
    </MainSidebarContext.Provider>
  );
};

export default MainSidebarContextWrapper;
